/**
 * Story 8.12 — Onglet Documents en lecture seule (modale profil membre)
 * Layout identique à TabDocuments : grille 8 colonnes, 1 cellule par section.
 */
import React, { useMemo } from 'react';
import DocumentsList from '../../../../../composants/DocumentsList/DocumentsList';
import type { ProfileData } from '../../../../../services/profileService';
import SectionPliable from '../../../../../composants/SectionPliable/SectionPliable';
import '../../../../../pages/Profil/tabs/TabDocuments.css';

interface ReadOnlyDocumentsProps {
    data: ProfileData;
}

const DOCUMENT_SECTIONS = [
    { type: 'kbis', label: 'Extrait Kbis' },
    { type: 'attestation_fiscale', label: 'Attestation fiscale' },
    { type: 'attestation_sociale', label: 'Attestation sociale' },
    { type: 'rc_pro', label: 'Assurance responsabilité civile' },
    { type: 'garantie_decennale', label: 'Assurance garantie décennale' },
    { type: 'qualifications', label: 'Certifications / qualifications' },
    { type: 'cv_equipe', label: "CV de l'équipe encadrante" },
    { type: 'rib', label: 'RIB' },
];

const CONTRACT_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);

const CCTP_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
);

const CCTP_VARIANTS = [
    { key: 'cctp-travaux', label: 'CCTP Travaux', keyword: 'travaux' },
    { key: 'cctp-ingenierie', label: 'CCTP Ingénierie', keyword: 'ingenierie' },
];

const ReadOnlyDocuments: React.FC<ReadOnlyDocumentsProps> = ({ data }) => {
    const { documents } = data;

    const docsByType = useMemo(() => {
        if (!documents || documents.length === 0) return {};
        const map: Record<string, typeof documents> = {};
        for (const doc of documents) {
            const t = doc.type || 'autre';
            if (!map[t]) {
                map[t] = [doc];
            } else {
                const existing = map[t][0];
                const existingDate = new Date(existing.created_at || 0).getTime();
                const newDate = new Date(doc.created_at || 0).getTime();
                if (newDate > existingDate) map[t] = [doc];
            }
        }
        return map;
    }, [documents]);

    const formatForDocsList = (docs: typeof documents) =>
        docs.map(doc => ({
            id: doc.id,
            type: doc.type_label || doc.type,
            updated_at: doc.created_at || doc.updated_at || new Date().toISOString(),
            key: doc.key,
            category: 'entreprise' as const,
            filename: doc.filename,
        }));

    const hasAC = (data.contrats_ac && data.contrats_ac.length > 0) ||
        (data.documents_communs && data.documents_communs.length > 0);

    return (
        <div className="readonly-tab readonly-documents">

            {/* Chapitre : Documents obligatoires */}
            <SectionPliable title="Documents obligatoires" className="readonly-section-pliable">
                <div className="chapter-content chapter-content--grid4">
                    {DOCUMENT_SECTIONS.map(({ type, label }) => {
                        const sectionDocs = docsByType[type] || [];
                        return (
                            <section key={type} className="document-section">
                                <div className="section-content">
                                    <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
                                        {label}
                                    </h2>
                                    {sectionDocs.length > 0 ? (
                                        <DocumentsList
                                            documents={formatForDocsList(sectionDocs)}
                                            variant="inline"
                                            iconColor="grey"
                                            hideLabel
                                        />
                                    ) : (
                                        <p className="readonly-doc-section-empty">Aucun document</p>
                                    )}
                                </div>
                            </section>
                        );
                    })}
                </div>
            </SectionPliable>

            {/* Chapitre : Accord-cadre */}
            {hasAC && (
                <SectionPliable title="Accord-cadre édition 2025-2026" className="readonly-section-pliable">
                    <div className="chapter-content chapter-content--grid4">

                        {/* 1 cellule par contrat */}
                        {(data.contrats_ac || [])
                            .filter(c => c.documents?.some(d => d.binaire))
                            .map(c => {
                                const doc = c.documents.find(d => d.binaire)!;
                                const lotCode = c.lot_technique?.code || '';
                                const titre = `CCAP + AE (lot ${lotCode}) : contrat N°${c.numero || c.id}`;
                                return (
                                    <section key={`contrat-${c.id}`} className="document-section">
                                        <div className="section-content">
                                            <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
                                                {titre}
                                            </h2>
                                            <DocumentsList
                                                documents={[{
                                                    id: c.id,
                                                    type: titre,
                                                    updated_at: c.updated_at || new Date().toISOString(),
                                                    category: 'accord_cadre' as const,
                                                    filename: doc.nom || `Contrat_${c.id}.pdf`,
                                                    binaire: doc.binaire,
                                                }]}
                                                variant="inline"
                                                iconColor="grey"
                                                hideLabel
                                                icon={CONTRACT_ICON}
                                            />
                                        </div>
                                    </section>
                                );
                            })}

                        {/* 2 cellules CCTP */}
                        {CCTP_VARIANTS.map(({ key, label, keyword }) => {
                            const docs = (data.documents_communs || []).filter(doc => {
                                const normalized = doc.type?.toLowerCase()
                                    .replace(/[-\s]/g, '')
                                    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
                                return normalized.includes('cctp') && normalized.includes(keyword);
                            });
                            const formattedDocs = docs.map(doc => ({
                                id: doc.id,
                                type: doc.type || label,
                                updated_at: doc.updated_at || new Date().toISOString(),
                                category: 'commun' as const,
                                filename: doc.nom || `${label}.pdf`,
                            }));
                            return (
                                <section key={key} className="document-section">
                                    <div className="section-content">
                                        <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
                                            {label}
                                        </h2>
                                        <DocumentsList
                                            documents={formattedDocs}
                                            variant="inline"
                                            iconColor="grey"
                                            hideLabel
                                            icon={CCTP_ICON}
                                        />
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </SectionPliable>
            )}

            {/* Aucun document du tout */}
            {(!documents || documents.length === 0) && !hasAC && (
                <SectionPliable title="Documents" className="readonly-section-pliable">
                    <div className="chapter-content">
                        <p className="readonly-doc-section-empty">Aucun document uploadé.</p>
                    </div>
                </SectionPliable>
            )}
        </div>
    );
};

export default ReadOnlyDocuments;
